import { setEditMode, setSignUpStep } from '../../../../store/signUp/slice';
import { setProfileDataThunk } from '../../../../store/signUp/thunks';
import { EditIcon, TypographyLogo, WarningIcon } from '../../../../assets/icons';
import { uploadFileThunk } from '../../../../store/general/thunks';
import { objectURLToFile } from '../../../../utils/generalHelpers';
import { useSelector, useDispatch } from '../../../../store/store';
import MotionWrapper from '../../../../layouts/MotionWrapper';
import { SignUpStep } from '../../../../store/signUp/types';
import CheckBox from '../../../../components/CheckBox';
import Button from '../../../../components/Button';
import styles from './style.module.scss';
import { v4 as uuid } from 'uuid';
import { useState } from 'react';
import {
    selectLogoImage,
    selectFetchStatus,
    selectEmployerInfo,
} from '../../../../store/signUp/selectors';

function EmployerReviewInfo() {
    const dispatch = useDispatch();
    const [agree, setAgree] = useState(false);
    const logoImage = useSelector(selectLogoImage);
    const fetchStatus = useSelector(selectFetchStatus);
    const employerInfo = useSelector(selectEmployerInfo);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const editInfo = (step: SignUpStep) => {
        dispatch(setEditMode(true));
        dispatch(setSignUpStep(step));
    };

    const submit = async () => {
        if (!employerInfo) return;
        const body = { ...employerInfo };

        if (logoImage) {
            try {
                setUploadingPhoto(true);
                const companyLogoKey = `${uuid()}.png`;
                const logoImageFile = await objectURLToFile(logoImage, 'logo_image.png', 'image/png');
                logoImageFile && await dispatch(uploadFileThunk({
                    file: logoImageFile,
                    accessLevel: 'guest',
                    fileName: `companies-logo-images/${companyLogoKey}`,
                }));

                body.companyLogo = companyLogoKey;
                setUploadingPhoto(false);
            } catch (e: unknown) {
                setUploadingPhoto(false);
                console.warn(e);
            }
        }

        dispatch(setProfileDataThunk(body));
    };

    return (
        <MotionWrapper className={styles.wrapper}>
            <div className={styles.head}>
                <TypographyLogo />
                <span className={styles.head_devider} />
                <span className={styles.head_title}>Sign Up</span>
            </div>
            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Company information</span>
                    <EditIcon onClick={() => editInfo('employerInfo')} />
                </div>
                <div className={styles.info_summary}>
                    <div className={styles.info_summary_item}>
                        <span>Full Name</span>
                        <span>{employerInfo?.firstName} {employerInfo?.lastName}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>phone Number</span>
                        <span>{employerInfo?.phoneNumber}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Company Name</span>
                        <span>{employerInfo?.companyName}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Company Size</span>
                        <span>{employerInfo?.companySize.title}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Employer Role</span>
                        <span>{employerInfo?.employerRole.title}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Company Website</span>
                        <span>{employerInfo?.companyWebsite}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Address</span>
                        <span>{employerInfo?.address}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Country</span>
                        <span>{employerInfo?.country}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>State</span>
                        <span>{employerInfo?.state}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>City</span>
                        <span>{employerInfo?.city}</span>
                    </div>
                    <div className={styles.info_summary_item}>
                        <span>Zip Code</span>
                        <span>{employerInfo?.zipCode}</span>
                    </div>
                </div>
            </div>

            <div className={styles.section}>
                <div className={styles.titleBar}>
                    <span>Logo image</span>
                    <EditIcon onClick={() => editInfo('uploadLogo')} />
                </div>
                <div className={styles.info_document}>
                    {logoImage ? <img src={logoImage} /> :
                        <div className={styles.info_document_warning}>
                            <WarningIcon />
                            <span>You did not upload logo image</span>
                        </div>}
                </div>
            </div>

            <div className={styles.agreement}>
                <CheckBox
                    text='By clicking here your are indicating 
                that you have read and entered your information correctly.'
                    checked={agree}
                    setChecked={setAgree}
                />
            </div>
            <Button
                type='Primary'
                text='Confirm'
                disabled={!agree}
                onClick={submit}
                className={styles.submit}
                loading={uploadingPhoto || (fetchStatus === 'Pending')}
            />
        </MotionWrapper>
    );
};

export default EmployerReviewInfo;
